import {
  buildPermissionBusinessFieldRows,
  permissionTargetForBusinessField,
} from '../state/businessFieldDirectory';
import type {
  CpqRole,
  DisposablePermissionGovernance,
  DisposablePermissionScope,
  QuoteFieldDefinitionRecord,
  RoleCatalogRecord,
  UserCatalogRecord,
} from '../state/types';

/** Disposable-only presentation fixture. It is never a formal RBAC adapter. */
export type PreviewQuoteState = 'draft' | 'submitted';
export type ApprovedScopePreview =
  | 'all'
  | 'department'
  | 'department_and_descendants'
  | 'specified_departments'
  | 'self'
  | 'self_assigned'
  | 'none';
export type PreviewDecision = 'allow' | 'deny' | 'pending';

export interface PreviewAction {
  id: string;
  label: string;
  permission: string;
  action: string;
  scope: ApprovedScopePreview;
  baseAllowed: boolean;
  explicitDeny?: boolean;
  reason: string;
}
export interface PreviewFeature {
  id: string;
  label: string;
  group: string;
  enabled: boolean;
  actions: PreviewAction[];
}
export interface PreviewChannel {
  id: string;
  label: string;
  permission: string;
  scope?: ApprovedScopePreview;
  decision: PreviewDecision;
  policy?: string;
  reason: string;
}
export interface DisposablePermissionScenario {
  id: string;
  roleId: string;
  roleKey: CpqRole;
  label: string;
  description: string;
  status: 'active' | 'disabled';
  memberCount: number;
  legacyMockDataScopeLabel: string;
  permissionVersion: string;
  sessionPermissionVersion: string;
  /** Explicit Disposable field presentation result derived from the approved row profile. */
  demoFieldPreview: { sensitiveRead: 'mask' | 'unavailable' };
  capabilities: {
    systemPreset: boolean;
    fieldDefinition: boolean;
    personalLayout: boolean;
    permissionPolicyProtected: boolean;
  };
  features: PreviewFeature[];
  channels: PreviewChannel[];
}
export interface ViewerPresentationCapabilities {
  canEditPreview: boolean;
  canOpenLegacyDirectory: boolean;
  canCreatePreviewRole: boolean;
  canPreviewMemberWrite: boolean;
}

const viewerRules: Record<CpqRole, ViewerPresentationCapabilities> = {
  super_admin: {
    canEditPreview: true,
    canOpenLegacyDirectory: true,
    canCreatePreviewRole: true,
    canPreviewMemberWrite: true,
  },
  admin: {
    canEditPreview: false,
    canOpenLegacyDirectory: false,
    canCreatePreviewRole: false,
    canPreviewMemberWrite: false,
  },
  manager: {
    canEditPreview: false,
    canOpenLegacyDirectory: false,
    canCreatePreviewRole: false,
    canPreviewMemberWrite: false,
  },
  sales: {
    canEditPreview: false,
    canOpenLegacyDirectory: false,
    canCreatePreviewRole: false,
    canPreviewMemberWrite: false,
  },
  tracker: {
    canEditPreview: false,
    canOpenLegacyDirectory: false,
    canCreatePreviewRole: false,
    canPreviewMemberWrite: false,
  },
  exec: {
    canEditPreview: false,
    canOpenLegacyDirectory: false,
    canCreatePreviewRole: false,
    canPreviewMemberWrite: false,
  },
  ry_sales: {
    canEditPreview: false,
    canOpenLegacyDirectory: false,
    canCreatePreviewRole: false,
    canPreviewMemberWrite: false,
  },
};

/** Stable IDs from the Disposable seed; unknown/custom IDs never inherit privileged presentation. */
const systemPreviewRoleMetadata: Record<
  string,
  DisposablePermissionScenario['capabilities']
> = {
  'ROLE-SUPER-ADMIN': {
    systemPreset: true,
    fieldDefinition: true,
    personalLayout: true,
    permissionPolicyProtected: true,
  },
  'ROLE-ADMIN': {
    systemPreset: true,
    fieldDefinition: false,
    personalLayout: true,
    permissionPolicyProtected: false,
  },
  'ROLE-MANAGER': {
    systemPreset: true,
    fieldDefinition: false,
    personalLayout: false,
    permissionPolicyProtected: false,
  },
  'ROLE-SALES': {
    systemPreset: true,
    fieldDefinition: false,
    personalLayout: false,
    permissionPolicyProtected: false,
  },
  'ROLE-TRACKER': {
    systemPreset: true,
    fieldDefinition: false,
    personalLayout: false,
    permissionPolicyProtected: false,
  },
  'ROLE-EXEC': {
    systemPreset: true,
    fieldDefinition: false,
    personalLayout: false,
    permissionPolicyProtected: false,
  },
  'ROLE-RY': {
    systemPreset: true,
    fieldDefinition: false,
    personalLayout: false,
    permissionPolicyProtected: false,
  },
};

const scopeText: Record<ApprovedScopePreview, string> = {
  all: '全部数据',
  department: '本部门',
  department_and_descendants: '本部门及下级部门',
  specified_departments: '指定部门',
  self: '仅本人负责的数据',
  self_assigned: '仅本人负责的数据',
  none: '无数据范围/不适用',
};

/** Disposable display copy only; permission codes remain the source identifier. */
const permissionDisplayNames: Record<string, string> = {
  'identity.user.manage': '管理用户',
  'identity.role.manage': '管理角色',
  'customer.read': '查看客户',
  'customer.manage': '维护客户',
  'catalog.read': '查看产品目录',
  'catalog.manage': '维护产品目录',
  'solution.read': '查看方案资料',
  'solution.manage': '维护方案资料',
  'quote.create': '新建报价',
  'quote.read': '查看报价',
  'quote.edit': '编辑报价',
  'quote.finalize': '定稿报价',
  'quote.share.internal': '内部分享报价',
  'quote.share.external': '创建公开链接',
  'quote.submit': '提交报价',
  'quote.export': '导出报价',
  'inventory.read': '查看库存',
  'inventory.adjust': '调整库存',
};

type ApprovedRow = readonly [
  string,
  ApprovedScopePreview | 'none',
  string,
  string,
  'allow' | 'deny',
];

/** Mechanical Disposable presentation copy of the approved matrix rows used by this UI. */
const approvedMatrix: Record<CpqRole, ApprovedRow[]> = {
  super_admin: [
    [
      'identity.user.manage',
      'all',
      'pii_full',
      'USER_ACTIVATE;USER_DISABLE;SESSION_REVOKE',
      'allow',
    ],
    [
      'identity.role.manage',
      'all',
      'permission_full',
      'ROLE_CREATE;ROLE_UPDATE;POLICY_PUBLISH',
      'allow',
    ],
    ['customer.read', 'all', 'pii_full', 'CUSTOMER_READ', 'allow'],
    [
      'customer.manage',
      'all',
      'pii_full',
      'CUSTOMER_CREATE;CUSTOMER_UPDATE;CONTACT_MANAGE',
      'allow',
    ],
    [
      'catalog.read',
      'all',
      'price_sale;price_rental;price_service;cost_full',
      'CATALOG_READ',
      'allow',
    ],
    [
      'catalog.manage',
      'all',
      'price_full;cost_full',
      'PRODUCT_CREATE_ACTIVE;PRODUCT_MANAGE;SKU_MANAGE;PRICE_MANAGE;FIELD_MANAGE',
      'allow',
    ],
    ['solution.read', 'all', 'price_full;cost_full', 'SOLUTION_READ', 'allow'],
    [
      'solution.manage',
      'all',
      'price_full;cost_full',
      'PLAN_MANAGE;PACKAGE_MANAGE;REVISION_CREATE',
      'allow',
    ],
    [
      'quote.create',
      'all',
      'price_full;cost_full',
      'QUOTE_CREATE;RY_DRAFT_CLAIM',
      'allow',
    ],
    ['quote.read', 'all', 'price_full;cost_full', 'QUOTE_READ', 'allow'],
    [
      'quote.edit',
      'all',
      'price_full;cost_full',
      'DRAFT_EDIT;COLLABORATION_EDIT',
      'allow',
    ],
    ['quote.finalize', 'all', 'price_full;cost_full', 'FINALIZE', 'allow'],
    [
      'quote.share.internal',
      'all',
      'price_full;cost_full',
      'INTERNAL_SHARE',
      'allow',
    ],
    [
      'quote.share.external',
      'all',
      'public_projection_config',
      'SHARE_CREATE;SHARE_UPDATE;SHARE_REVOKE;SHARE_PUBLISH_REVISION',
      'allow',
    ],
    ['quote.submit', 'all', 'price_full;cost_full', 'SUBMIT', 'allow'],
    [
      'quote.export',
      'all',
      'export_all_allowed_fields',
      'EXPORT_CREATE',
      'allow',
    ],
    ['inventory.read', 'all', 'inventory_full', 'INVENTORY_READ', 'allow'],
    ['inventory.adjust', 'all', 'inventory_full', 'INVENTORY_ADJUST', 'allow'],
  ],
  admin: [
    ['identity.user.manage', 'none', 'deny', 'none', 'deny'],
    ['identity.role.manage', 'none', 'deny', 'none', 'deny'],
    ['customer.read', 'all', 'pii_full', 'CUSTOMER_READ', 'allow'],
    [
      'customer.manage',
      'all',
      'pii_full',
      'CUSTOMER_CREATE;CUSTOMER_UPDATE;CONTACT_MANAGE',
      'allow',
    ],
    [
      'catalog.read',
      'all',
      'price_sale;price_rental;price_service;cost_full',
      'CATALOG_READ',
      'allow',
    ],
    [
      'catalog.manage',
      'all',
      'price_full;cost_full',
      'PRODUCT_CREATE_ACTIVE;PRODUCT_MANAGE;SKU_MANAGE;PRICE_MANAGE',
      'allow',
    ],
    ['solution.read', 'all', 'price_full;cost_full', 'SOLUTION_READ', 'allow'],
    [
      'solution.manage',
      'all',
      'price_full;cost_full',
      'PLAN_MANAGE;PACKAGE_MANAGE;REVISION_CREATE',
      'allow',
    ],
    [
      'quote.create',
      'all',
      'price_full;cost_full',
      'QUOTE_CREATE;RY_DRAFT_CLAIM',
      'allow',
    ],
    ['quote.read', 'all', 'price_full;cost_full', 'QUOTE_READ', 'allow'],
    [
      'quote.edit',
      'all',
      'price_full;cost_full',
      'DRAFT_EDIT;COLLABORATION_EDIT',
      'allow',
    ],
    ['quote.finalize', 'all', 'price_full;cost_full', 'FINALIZE', 'allow'],
    [
      'quote.share.internal',
      'all',
      'price_full;cost_full',
      'INTERNAL_SHARE',
      'allow',
    ],
    [
      'quote.share.external',
      'all',
      'public_projection_config',
      'SHARE_CREATE;SHARE_UPDATE;SHARE_REVOKE;SHARE_PUBLISH_REVISION',
      'allow',
    ],
    ['quote.submit', 'all', 'price_full;cost_full', 'SUBMIT', 'allow'],
    [
      'quote.export',
      'all',
      'export_all_allowed_fields',
      'EXPORT_CREATE',
      'allow',
    ],
    ['inventory.read', 'all', 'inventory_full', 'INVENTORY_READ', 'allow'],
    ['inventory.adjust', 'all', 'inventory_full', 'INVENTORY_ADJUST', 'allow'],
  ],
  manager: [
    ['identity.user.manage', 'none', 'deny', 'none', 'deny'],
    ['identity.role.manage', 'none', 'deny', 'none', 'deny'],
    ['customer.read', 'department', 'pii_full', 'CUSTOMER_READ', 'allow'],
    [
      'customer.manage',
      'department',
      'pii_full',
      'CUSTOMER_CREATE;CUSTOMER_UPDATE;CONTACT_MANAGE',
      'allow',
    ],
    ['catalog.read', 'all', 'price_full;cost_full', 'CATALOG_READ', 'allow'],
    [
      'catalog.manage',
      'all',
      'price_full;cost_full',
      'PRODUCT_CREATE_ACTIVE;PRODUCT_MANAGE;SKU_MANAGE;PRICE_MANAGE;FIELD_MANAGE',
      'allow',
    ],
    ['solution.read', 'all', 'price_full;cost_full', 'SOLUTION_READ', 'allow'],
    [
      'solution.manage',
      'all',
      'price_full;cost_full',
      'PLAN_MANAGE;PACKAGE_MANAGE;REVISION_CREATE',
      'allow',
    ],
    [
      'quote.create',
      'department',
      'price_full;cost_full',
      'QUOTE_CREATE;RY_DRAFT_CLAIM',
      'allow',
    ],
    ['quote.read', 'department', 'price_full;cost_full', 'QUOTE_READ', 'allow'],
    [
      'quote.edit',
      'department',
      'price_full;cost_full',
      'DRAFT_EDIT;COLLABORATION_EDIT',
      'allow',
    ],
    [
      'quote.finalize',
      'department',
      'price_full;cost_full',
      'FINALIZE',
      'allow',
    ],
    [
      'quote.share.internal',
      'department',
      'price_full;cost_full',
      'INTERNAL_SHARE',
      'allow',
    ],
    [
      'quote.share.external',
      'department',
      'public_projection_config',
      'SHARE_CREATE;SHARE_UPDATE;SHARE_REVOKE;SHARE_PUBLISH_REVISION',
      'allow',
    ],
    ['quote.submit', 'department', 'price_full;cost_full', 'SUBMIT', 'allow'],
    [
      'quote.export',
      'department',
      'export_allowed_fields',
      'EXPORT_CREATE',
      'allow',
    ],
    ['inventory.read', 'all', 'inventory_full', 'INVENTORY_READ', 'allow'],
    ['inventory.adjust', 'all', 'inventory_full', 'INVENTORY_ADJUST', 'allow'],
  ],
  sales: [
    ['customer.read', 'self_assigned', 'pii_full', 'CUSTOMER_READ', 'allow'],
    [
      'customer.manage',
      'self_assigned',
      'pii_full',
      'CUSTOMER_CREATE;CUSTOMER_UPDATE;CONTACT_MANAGE',
      'allow',
    ],
    [
      'catalog.read',
      'all',
      'price_sale;price_rental;price_service',
      'CATALOG_READ',
      'allow',
    ],
    ['catalog.manage', 'none', 'deny', 'none', 'deny'],
    [
      'solution.read',
      'all',
      'price_sale;price_rental;price_service',
      'SOLUTION_READ',
      'allow',
    ],
    ['solution.manage', 'none', 'deny', 'none', 'deny'],
    [
      'quote.create',
      'self',
      'price_sale;price_rental;price_service',
      'QUOTE_CREATE',
      'allow',
    ],
    [
      'quote.read',
      'self_assigned',
      'price_sale;price_rental;price_service',
      'QUOTE_READ',
      'allow',
    ],
    [
      'quote.edit',
      'self_assigned',
      'price_sale;price_rental;price_service',
      'DRAFT_EDIT;COLLABORATION_EDIT',
      'allow',
    ],
    [
      'quote.finalize',
      'self_assigned',
      'price_sale;price_rental;price_service',
      'FINALIZE',
      'allow',
    ],
    [
      'quote.share.internal',
      'self_assigned',
      'price_sale;price_rental;price_service',
      'INTERNAL_SHARE',
      'allow',
    ],
    [
      'quote.share.external',
      'self_assigned',
      'public_projection_config',
      'SHARE_CREATE;SHARE_UPDATE;SHARE_REVOKE;SHARE_PUBLISH_REVISION',
      'allow',
    ],
    [
      'quote.submit',
      'self_assigned',
      'price_sale;price_rental;price_service',
      'SUBMIT',
      'allow',
    ],
    [
      'quote.export',
      'self_assigned',
      'export_non_cost_fields',
      'EXPORT_CREATE',
      'allow',
    ],
    [
      'inventory.read',
      'all',
      'inventory_quantity_only',
      'INVENTORY_READ',
      'allow',
    ],
    ['inventory.adjust', 'none', 'deny', 'none', 'deny'],
  ],
  tracker: [
    ['customer.read', 'department', 'pii_masked', 'CUSTOMER_READ', 'allow'],
    ['customer.manage', 'none', 'deny', 'none', 'deny'],
    [
      'catalog.read',
      'all',
      'price_sale;inventory_quantity',
      'CATALOG_READ',
      'allow',
    ],
    ['catalog.manage', 'none', 'deny', 'none', 'deny'],
    ['solution.read', 'all', 'price_sale', 'SOLUTION_READ', 'allow'],
    ['solution.manage', 'none', 'deny', 'none', 'deny'],
    ['quote.create', 'none', 'deny', 'none', 'deny'],
    [
      'quote.read',
      'department',
      'price_sale;inventory_quantity',
      'QUOTE_READ',
      'allow',
    ],
    ['quote.edit', 'none', 'deny', 'none', 'deny'],
    ['quote.finalize', 'none', 'deny', 'none', 'deny'],
    ['quote.share.internal', 'none', 'deny', 'none', 'deny'],
    ['quote.share.external', 'none', 'deny', 'none', 'deny'],
    ['quote.submit', 'none', 'deny', 'none', 'deny'],
    [
      'quote.export',
      'department',
      'export_non_cost_fields',
      'EXPORT_CREATE',
      'allow',
    ],
    [
      'inventory.read',
      'all',
      'inventory_quantity_only',
      'INVENTORY_READ',
      'allow',
    ],
    ['inventory.adjust', 'none', 'deny', 'none', 'deny'],
  ],
  exec: [
    ['customer.read', 'all', 'pii_masked', 'CUSTOMER_READ', 'allow'],
    ['customer.manage', 'none', 'deny', 'none', 'deny'],
    ['catalog.read', 'all', 'price_full;cost_full', 'CATALOG_READ', 'allow'],
    ['catalog.manage', 'none', 'deny', 'none', 'deny'],
    ['solution.read', 'all', 'price_full;cost_full', 'SOLUTION_READ', 'allow'],
    ['solution.manage', 'none', 'deny', 'none', 'deny'],
    ['quote.create', 'none', 'deny', 'none', 'deny'],
    ['quote.read', 'all', 'price_full;cost_full', 'QUOTE_READ', 'allow'],
    ['quote.edit', 'none', 'deny', 'none', 'deny'],
    ['quote.finalize', 'none', 'deny', 'none', 'deny'],
    ['quote.share.internal', 'none', 'deny', 'none', 'deny'],
    ['quote.share.external', 'none', 'deny', 'none', 'deny'],
    ['quote.submit', 'none', 'deny', 'none', 'deny'],
    [
      'quote.export',
      'all',
      'export_all_allowed_fields',
      'EXPORT_CREATE',
      'allow',
    ],
    ['inventory.read', 'all', 'inventory_full', 'INVENTORY_READ', 'allow'],
    ['inventory.adjust', 'none', 'deny', 'none', 'deny'],
  ],
  ry_sales: [
    ['customer.read', 'self', 'pii_own_submission', 'CUSTOMER_READ', 'allow'],
    ['customer.manage', 'none', 'deny', 'none', 'deny'],
    ['catalog.read', 'all', 'price_rental', 'CATALOG_READ', 'allow'],
    ['catalog.manage', 'none', 'deny', 'none', 'deny'],
    ['solution.read', 'all', 'price_rental', 'SOLUTION_READ', 'allow'],
    ['solution.manage', 'none', 'deny', 'none', 'deny'],
    ['quote.create', 'self', 'price_rental', 'RY_DRAFT_CREATE', 'allow'],
    [
      'quote.read',
      'self',
      'price_rental;progress_only',
      'QUOTE_PROGRESS_READ',
      'allow',
    ],
    ['quote.edit', 'self', 'price_rental', 'DRAFT_EDIT', 'allow'],
    ['quote.finalize', 'none', 'deny', 'none', 'deny'],
    ['quote.share.internal', 'none', 'deny', 'none', 'deny'],
    ['quote.share.external', 'none', 'deny', 'none', 'deny'],
    ['quote.submit', 'none', 'deny', 'none', 'deny'],
    [
      'quote.export',
      'self',
      'export_rental_non_cost',
      'EXPORT_CREATE',
      'allow',
    ],
    ['inventory.read', 'none', 'deny', 'none', 'deny'],
    ['inventory.adjust', 'none', 'deny', 'none', 'deny'],
  ],
};

const featureDefinitions: Array<[string, string, string, string[]]> = [
  ['workspace', '报价工作台', '报价业务', ['quote.read', 'quote.edit']],
  [
    'quotes',
    '报价管理',
    '报价业务',
    [
      'quote.create',
      'quote.read',
      'quote.edit',
      'quote.finalize',
      'quote.share.internal',
      'quote.share.external',
      'quote.submit',
      'quote.export',
    ],
  ],
  ['plans', '方案管理', '资料/目录', ['solution.read', 'solution.manage']],
  ['packages', '套餐管理', '资料/目录', ['solution.read', 'solution.manage']],
  ['devices', '设备管理', '资料/目录', ['catalog.read', 'catalog.manage']],
  ['customers', '客户管理', '运营', ['customer.read', 'customer.manage']],
  ['inventory', '库存管理', '运营', ['inventory.read', 'inventory.adjust']],
  [
    'permissions',
    '用户与权限',
    '后台管理',
    ['identity.user.manage', 'identity.role.manage'],
  ],
  ['settings', '系统设置', '后台管理', []],
  [
    'rental',
    '人人租简化报价',
    '报价业务',
    ['quote.create', 'quote.read', 'quote.edit'],
  ],
];

function moduleFeatures(role: CpqRole): PreviewFeature[] {
  const rows = new Map(approvedMatrix[role].map((row) => [row[0], row]));
  return featureDefinitions.map(([id, label, group, permissions]) => ({
    id,
    label,
    group,
    enabled: permissions.some(
      (permission) => rows.get(permission)?.[4] === 'allow',
    ),
    actions: permissions.map((permission) => {
      const row = rows.get(permission);
      if (!row)
        return {
          id: `${id}:${permission}`,
          label: '待正式操作合同接入',
          permission: 'pending',
          action: 'pending',
          scope: 'none' as ApprovedScopePreview,
          baseAllowed: false,
          explicitDeny: true,
          reason: '待正式合同接入',
        };
      const [code, scope, profile, actions, decision] = row;
      return {
        id: `${id}:${code}`,
        label: permissionDisplayNames[code] ?? '待正式操作合同接入',
        permission: code,
        action: actions,
        scope,
        baseAllowed: decision === 'allow',
        explicitDeny: decision === 'deny',
        reason: `${profile}；${actions}`,
      };
    }),
  }));
}

function channelsFor(role: CpqRole): PreviewChannel[] {
  const lookup = (permission: string) =>
    approvedMatrix[role].find((row) => row[0] === permission);
  const toChannel = (
    id: string,
    label: string,
    permission: string,
  ): PreviewChannel => {
    const row = lookup(permission);
    if (!row)
      return {
        id,
        label,
        permission,
        decision: 'pending',
        reason: '待正式操作合同接入',
      };
    const [, scope, profile, actions, decision] = row;
    return {
      id,
      label,
      permission,
      scope: scope === 'none' ? undefined : scope,
      decision,
      policy: profile,
      reason: actions,
    };
  };
  const exportChannel = toChannel('channel:pdf', '生成 PDF', 'quote.export');
  const publicLink = toChannel(
    'channel:public-link',
    '公开链接',
    'quote.share.external',
  );
  return [
    toChannel('channel:internal-read', '内部查看', 'quote.read'),
    toChannel('channel:internal-edit', '内部编辑', 'quote.edit'),
    toChannel('channel:internal-share', '内部分享', 'quote.share.internal'),
    publicLink,
    { ...exportChannel, reason: '复用报价导出动作；PDF 通道继续收窄允许字段' },
    {
      ...exportChannel,
      id: 'channel:excel',
      label: '导出 Excel',
      reason: '复用报价导出动作；Excel 通道继续收窄允许字段',
    },
    {
      ...exportChannel,
      id: 'channel:document',
      label: '报价文档',
      reason: '复用报价导出动作；报价文档通道继续收窄允许字段',
    },
  ];
}

export function createDisposablePermissionPreview(
  roles: RoleCatalogRecord[],
  users: UserCatalogRecord[],
  currentRole: CpqRole,
): {
  scenarios: DisposablePermissionScenario[];
  currentScenarioId: string;
  viewerPresentationCapabilities: ViewerPresentationCapabilities;
} {
  return {
    scenarios: roles.map((role) => {
      const quoteReadProfile =
        approvedMatrix[role.key].find((row) => row[0] === 'quote.read')?.[2] ??
        'deny';
      return {
        id: role.id,
        roleId: role.id,
        roleKey: role.key,
        label: role.label,
        description: role.description,
        status: role.status,
        memberCount: users.filter(
          (user) =>
            user.roleId === role.id ||
            (!user.roleId &&
              roles.filter((candidate) => candidate.key === user.role)
                .length === 1 &&
              user.role === role.key),
        ).length,
        legacyMockDataScopeLabel: role.dataScope,
        permissionVersion: '1',
        sessionPermissionVersion: '1',
        demoFieldPreview: {
          sensitiveRead: quoteReadProfile.includes('cost_full')
            ? 'mask'
            : 'unavailable',
        },
        capabilities: systemPreviewRoleMetadata[role.id] ?? {
          systemPreset: false,
          fieldDefinition: false,
          personalLayout: false,
          permissionPolicyProtected: false,
        },
        features: moduleFeatures(role.key),
        channels: channelsFor(role.key).map((channel) =>
          role.status === 'disabled'
            ? {
                ...channel,
                decision: 'deny' as PreviewDecision,
                reason: '角色已停用',
              }
            : channel,
        ),
      };
    }),
    currentScenarioId:
      roles.find((role) => role.key === currentRole)?.id ?? roles[0]?.id ?? '',
    viewerPresentationCapabilities: structuredClone(viewerRules[currentRole]),
  };
}

export function resolveMockActions(
  _scenario: DisposablePermissionScenario,
  features: PreviewFeature[],
  quoteState: PreviewQuoteState,
) {
  return features.flatMap((feature) =>
    feature.actions.map((action) => {
      const explicitDeny = Boolean(action.explicitDeny);
      const roleDisabled = _scenario.status === 'disabled';
      const featureDeny = !feature.enabled;
      const stateDeny =
        quoteState === 'submitted' && action.permission === 'quote.edit';
      const allowed = !(
        roleDisabled ||
        explicitDeny ||
        featureDeny ||
        stateDeny
      );
      const reason = roleDisabled
        ? '角色已停用'
        : explicitDeny
          ? action.reason
          : featureDeny
            ? '该功能未启用'
            : stateDeny
              ? '已提交报价不可编辑'
              : action.reason;
      return { ...action, feature: feature.label, allowed, reason };
    }),
  );
}

export function resolveMockFields(
  scenario: DisposablePermissionScenario,
  features: PreviewFeature[],
  quoteState: PreviewQuoteState,
) {
  const actions = resolveMockActions(scenario, features, quoteState);
  const readable = actions.some(
    (action) => action.permission === 'quote.read' && action.allowed,
  );
  const editable =
    readable &&
    actions.some(
      (action) => action.permission === 'quote.edit' && action.allowed,
    );
  const sensitiveUnavailable =
    scenario.demoFieldPreview.sensitiveRead === 'unavailable';
  return [
    {
      name: '演示普通字段',
      kind: 'ordinary',
      read: readable ? 'allow' : 'unavailable',
      write: editable ? 'allow' : 'deny',
      effect: !readable
        ? 'hidden / unavailable'
        : editable
          ? 'editable'
          : 'readonly',
    },
    {
      name: '演示敏感字段',
      kind: 'sensitive',
      read: !readable || sensitiveUnavailable ? 'unavailable' : 'mask',
      write: 'deny',
      effect:
        !readable || sensitiveUnavailable
          ? 'hidden / unavailable'
          : 'masked；mask策略：待正式合同提供',
    },
    {
      name: '演示未分类字段',
      kind: 'unclassified',
      read: 'unavailable',
      write: 'deny',
      effect: 'hidden / unavailable',
    },
    {
      name: '缺策略：fail-closed',
      kind: 'sensitive',
      read: 'unavailable',
      write: 'deny',
      effect: 'hidden / unavailable',
    },
  ];
}

export function previewScopeLabel(scope: ApprovedScopePreview) {
  return scopeText[scope];
}

export function approvedScopesForAction(
  base: ApprovedScopePreview,
): DisposablePermissionScope[] {
  const scopes: DisposablePermissionScope[] = [base];
  if (base === 'all')
    scopes.push(
      'department_and_descendants',
      'department',
      'specified_departments',
      'self',
      'none',
    );
  else if (base === 'department_and_descendants')
    scopes.push('department', 'specified_departments', 'self', 'none');
  else if (base === 'department')
    scopes.push('specified_departments', 'self', 'none');
  else if (base === 'self_assigned') scopes.push('self', 'none');
  else if (base !== 'none') scopes.push('none');
  return [...new Set(scopes)];
}

export function createInitialPermissionGovernance(
  roles: RoleCatalogRecord[],
  definitions: QuoteFieldDefinitionRecord[],
): DisposablePermissionGovernance {
  const fieldRows = buildPermissionBusinessFieldRows(definitions);
  const externallyDeniedByDefault = new Set([
    'definition:jky_code',
    'definition:retail_price_tax13',
    'definition:purchase_final_price_tax13',
    'definition:dynamic_cost',
    'definition:dynamic_cost_subtotal',
  ]);
  const costOnly = new Set([
    'definition:dynamic_cost',
    'definition:dynamic_cost_subtotal',
  ]);
  return {
    kind: 'disposable-permission-governance-v1',
    fieldChannelPolicyVersion: 2,
    permissionVersion: 1,
    roles: Object.fromEntries(
      roles.map((role) => {
        const features = moduleFeatures(role.key);
        const actionRows = features.flatMap((feature) => feature.actions);
        const isSuperAdmin = role.key === 'super_admin';
        const actionEnabled = (actionId?: string) =>
          Boolean(
            actionId &&
              actionRows.some(
                (action) =>
                  action.id === actionId &&
                  action.baseAllowed &&
                  !action.explicitDeny,
              ),
          );
        const quoteReadProfile =
          approvedMatrix[role.key].find(
            (row) => row[0] === 'quote.read',
          )?.[2] ?? 'deny';
        const fieldGrants = Object.fromEntries(
          fieldRows.map((field) => {
            const target = permissionTargetForBusinessField(field);
            const view =
              field.available &&
              (isSuperAdmin
                ? true
                : actionEnabled(target?.readActionId) &&
                  (!costOnly.has(field.id) ||
                    quoteReadProfile.includes('cost_full')));
            return [
              field.id,
              {
                view,
                edit:
                  view &&
                  field.editable &&
                  (isSuperAdmin || actionEnabled(target?.editActionId)),
                export:
                  view &&
                  (isSuperAdmin ||
                    (actionEnabled(target?.exportActionId) &&
                      !externallyDeniedByDefault.has(field.id))),
              },
            ];
          }),
        );
        return [
          role.id,
          {
            roleId: role.id,
            publishedVersion: 1,
            featureEnabled: Object.fromEntries(
              features.map((feature) => [feature.id, feature.enabled]),
            ),
            actions: Object.fromEntries(
              features
                .flatMap((feature) => feature.actions)
                .map((action) => [
                  action.id,
                  {
                    enabled: action.baseAllowed && !action.explicitDeny,
                    scope: action.scope,
                    specifiedDepartmentIds: [],
                  },
                ]),
            ),
            fieldGrants,
            channels: Object.fromEntries(
              channelsFor(role.key).map((channel) => [
                channel.id,
                {
                  enabled: channel.decision === 'allow',
                  fieldIds: fieldRows
                    .filter((field) => {
                      const grant = fieldGrants[field.id];
                      if (
                        !isSuperAdmin &&
                        channel.id === 'channel:public-link' &&
                        externallyDeniedByDefault.has(field.id)
                      ) {
                        return false;
                      }
                      return channel.id === 'channel:internal-edit'
                        ? grant.edit
                        : [
                              'channel:pdf',
                              'channel:excel',
                              'channel:document',
                            ].includes(channel.id)
                          ? grant.export
                          : grant.view;
                    })
                    .map((field) => field.id),
                },
              ]),
            ),
            memberAdjustments: {},
          },
        ];
      }),
    ),
  };
}

export function approvedScenarioForRole(role: CpqRole): {
  features: PreviewFeature[];
  channels: PreviewChannel[];
} {
  return { features: moduleFeatures(role), channels: channelsFor(role) };
}
